"use client"

import { startTransition } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

import {
  CatalogFilterKey,
  CatalogFilters,
  getCatalogFilterOptions,
} from "@lib/util/product-specs"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"

type FilterOptions = ReturnType<typeof getCatalogFilterOptions>

type CatalogFilterBarProps = {
  sortBy: SortOptions
  filters: CatalogFilters
  options: FilterOptions
}

const sortOptions: Array<{ value: SortOptions; label: string }> = [
  { value: "created_at", label: "Latest" },
  { value: "price_asc", label: "Price Low -> High" },
  { value: "price_desc", label: "Price High -> Low" },
]

/** Parent → child order (like province → district → tambon). */
const filterCascade: CatalogFilterKey[] = [
  "brand",
  "category",
  "subcategory",
  "architecture",
  "wired",
  "poe",
  "sfp",
  "ros",
]

const filterLabels: Record<CatalogFilterKey, string> = {
  brand: "Brand",
  category: "Category",
  subcategory: "Subcategory",
  architecture: "Architecture",
  wired: "Wired Interfaces",
  poe: "PoE",
  sfp: "SFP/SFP+",
  ros: "Operating System",
}

const filterKeys = filterCascade

export default function CatalogFilterBar({
  sortBy,
  filters,
  options,
}: CatalogFilterBarProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const updateParam = (name: string, value: string) => {
    const params = new URLSearchParams(searchParams)

    if (value) {
      params.set(name, value)
    } else {
      params.delete(name)
    }

    // Changing a parent clears children (cascading reset).
    const cascadeIndex = filterCascade.indexOf(name as CatalogFilterKey)
    if (cascadeIndex >= 0) {
      filterCascade.slice(cascadeIndex + 1).forEach((key) => params.delete(key))
    }

    params.delete("page")

    startTransition(() => {
      const query = params.toString()
      router.push(query ? `${pathname}?${query}` : pathname)
    })
  }

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams)
    // Keep `q` and `sortBy` so catalog clears don't drop an active search.
    filterKeys.forEach((key) => params.delete(key))
    params.delete("page")

    startTransition(() => {
      const query = params.toString()
      router.push(query ? `${pathname}?${query}` : pathname)
    })
  }

  const hasActiveFilters = filterKeys.some((key) => filters[key])

  return (
    <div className="mb-8 flex flex-col gap-3 rounded-xl border border-[#e8eaed] bg-white px-4 py-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] medium:flex-row medium:items-end medium:justify-between">
      <div className="grid grid-cols-2 gap-2 small:grid-cols-3 medium:flex medium:flex-wrap">
        {filterKeys.map((key) => {
          const selected = filters[key] || ""
          const choices = options[key] || []
          // Keep a stale URL value visible until cascade clear runs.
          const optionList =
            selected && !choices.includes(selected)
              ? [selected, ...choices]
              : choices

          return (
            <label key={key} className="catalog-filter-select">
              <span>{filterLabels[key]}</span>
              <select
                value={selected}
                onChange={(event) => updateParam(key, event.target.value)}
              >
                <option value="">All</option>
                {optionList.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          )
        })}
      </div>

      <div className="flex items-center gap-2">
        {hasActiveFilters && (
          <button
            type="button"
            className="catalog-filter-reset"
            onClick={clearFilters}
          >
            Clear
          </button>
        )}
        <label className="catalog-filter-select">
          <span>Sort</span>
          <select
            value={sortBy}
            onChange={(event) => updateParam("sortBy", event.target.value)}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  )
}
